/**
 * Faces for the players on every board.
 *
 * A row of names is a list; a row of faces is a roster. Scanning six leagues on
 * a Sunday you are not reading names, you are looking for a guy — and the
 * fastest way to find him is to recognise him. Every view here renders the same
 * `PlayerAvatar`, so a player looks the same on the draft board, in his
 * matchup, and on his dynasty row.
 *
 * **Where the pictures come from.** Sleeper, on both counts:
 *
 *   - headshots at `content/nfl/players/<player_id>.jpg`, keyed on Sleeper's
 *     own player id, which covers ~99% of the players any board shows;
 *   - team logos at `images/team_logos/nfl/<team>.png`, lowercase, which is
 *     what a defense gets — a team has no headshot, and all 32 codes resolve
 *     off `canonicalTeam()`.
 *
 * ESPN was the other candidate and lost on coverage: probing a spread of ranked
 * players, Sleeper served a face for 63 of 64 and ESPN for 14, mostly because
 * so many rows carry no `espn_id` at all. One origin for both kinds of image is
 * the bonus.
 *
 * **Two ways to get an id.** The rooting dashboard and the dynasty panel read
 * rosters straight from Sleeper, so they already hold the id and pass it in.
 * The draft board does not — its rows come from the expert consensus and are
 * keyed by name and position — so it looks the id up in
 * `public/player-headshots.json`, a small build-time map from `boardRowId` to
 * Sleeper id. See `scripts/fetch-player-headshots.mjs` for why that file exists
 * rather than the app loading Sleeper's 14MB player dump on the one view that
 * has no other use for it.
 *
 * Nothing here throws and nothing blocks a render. A missing id, a failed map
 * fetch, or a 403 from the CDN all end the same way: the avatar falls back,
 * first to the team logo and then to initials, and the board draws regardless.
 */

import { boardRowId, canonicalTeam } from './playerMatch.js';

const HEADSHOTS_JSON = '/player-headshots.json';

const SLEEPER_HEADSHOT = 'https://sleepercdn.com/content/nfl/players/';
const SLEEPER_TEAM_LOGO = 'https://sleepercdn.com/images/team_logos/nfl/';

/** Board and Sleeper spellings of the two team-shaped positions. */
const TEAM_POSITIONS = new Set(['DST', 'DEF']);

export const isTeamPosition = (position) => TEAM_POSITIONS.has(String(position || '').toUpperCase());

/**
 * A Sleeper player id, or null.
 *
 * Sleeper keys its defenses by team abbreviation rather than a number, so a
 * "player id" of `BUF` is a team and must not be sent to the headshot path —
 * it 403s, and the row would flash a broken image before falling back. Numeric
 * ids only.
 */
const asPlayerId = (sleeperId) => {
  const id = String(sleeperId ?? '').trim();
  return /^\d+$/.test(id) ? id : null;
};

export const headshotUrl = (sleeperId) => {
  const id = asPlayerId(sleeperId);
  return id ? `${SLEEPER_HEADSHOT}${id}.jpg` : null;
};

export const teamLogoUrl = (team) => {
  const code = canonicalTeam(team);
  return code ? `${SLEEPER_TEAM_LOGO}${code.toLowerCase()}.png` : null;
};

/**
 * Up to two letters for a player with no picture on either CDN.
 *
 * Initials rather than a silhouette: on a bench row they still tell two players
 * apart, and they never look like a failed image.
 */
export const playerInitials = (name) => {
  const words = String(name || '')
    .replace(/[^A-Za-z\s'-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

let headshotMapCache = null;
let headshotMapPromise = null;

/**
 * The name-keyed id map, fetched once and never retried.
 *
 * A failure resolves to an empty map instead of rejecting. Faces are a nicety
 * on a board whose numbers are the point, so a 404 on this file costs pictures
 * on the draft view and nothing else — every caller treats "no id" as a
 * fallback, which is the same path a player Sleeper has no photo of takes.
 */
export const loadHeadshotMap = () => {
  if (headshotMapCache) return Promise.resolve(headshotMapCache);
  if (headshotMapPromise) return headshotMapPromise;

  headshotMapPromise = fetch(HEADSHOTS_JSON)
    .then((response) => (response.ok ? response.json() : null))
    .then((payload) => {
      headshotMapCache = payload?.byId && typeof payload.byId === 'object' ? payload.byId : {};
      return headshotMapCache;
    })
    .catch(() => {
      headshotMapCache = {};
      return headshotMapCache;
    });

  return headshotMapPromise;
};

/**
 * Resolve a board row to a Sleeper id through the map.
 *
 * `row.id` is already a `boardRowId`, which is exactly how the map is keyed, so
 * the common case is one object lookup. Rebuilding the key from name and
 * position is the fallback for a row from somewhere else — a KTC asset, a
 * synced draft pick — that carries a name but not a board id.
 */
export const resolveSleeperId = (row, headshotMap) => {
  if (!row || !headshotMap) return null;
  if (isTeamPosition(row.position)) return null;

  const direct = asPlayerId(row.sleeperId ?? row.playerId);
  if (direct) return direct;

  if (row.id && headshotMap[row.id]) return asPlayerId(headshotMap[row.id]);

  if (!row.name || !row.position) return null;
  const key = boardRowId(row.name, row.position);
  return key ? asPlayerId(headshotMap[key]) : null;
};

/**
 * The image chain for one player, best first.
 *
 * The avatar walks this list on error rather than picking one URL up front, so
 * a player the map has an id for but the CDN has no photo of still ends up
 * wearing his team's logo instead of a gap.
 */
export const playerImageSources = ({ sleeperId, team, position } = {}) => {
  const sources = [];
  if (!isTeamPosition(position)) {
    const headshot = headshotUrl(sleeperId);
    if (headshot) sources.push(headshot);
  }
  const logo = teamLogoUrl(team);
  if (logo) sources.push(logo);
  return sources;
};
